// src/pages/SystemAdminPage.tsx
//
// WS-A4.2 (read-only MVP) + WS-A4.3a (editable config + features)
// + WS-A4.3b (user mgmt + per-user feature overrides)
// + WS-A4.3c (scheduler CRUD)
// + WS-A4.3d (suspend/activate toggle on TenantHeader).
// Lists tenants from Supabase on the left; per-tenant drilldown on the
// right with Health / Config / Features / Users / Scheduler / Audit tabs.
//
// Editing model:
//   - Config: "+ Add Config" opens a modal with key/value/type fields.
//             "Edit" on each row reopens that modal with the row prefilled.
//             Save -> adminApi.putConfig(slug) -> React Query invalidate.
//   - Features: "+ Add Feature" opens a modal for new feature_key + initial
//             state. Each toggle is clickable -> adminApi.putFeature(slug)
//             with optimistic update.
//   - Users: "+ Invite" opens a modal (auth0Sub + email + role). Each row
//             has "Overrides" (opens grid of tenant features with 3-way
//             control: default / force-on / force-off) and "Revoke".
//   - Scheduler: "+ Add" opens a modal with eventId/sendType/time/days/
//             active fields. Days-of-week is a 7-char positional mask
//             ('MTWTFSS' = every day, 'MTWTF--' = weekdays). TZ defaults
//             to the tenant directory time_zone.
//   - TenantHeader: sysadmin-only Suspend/Activate button (writes Supabase
//             via /api/tenants/:slug/status; suspend requires a reason).

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, RefreshCw, Shield, Server, AlertCircle,
  Building2, Activity, Settings,
  ToggleLeft, ToggleRight, Clock, Plus, Pencil, Loader2,
  Users as UsersIcon, Trash2, SlidersHorizontal,
  Power, PowerOff, Calendar,
} from 'lucide-react';
import { useAuthContext } from '../hooks/useAuth';
import { supabase, supabaseConfigured } from '../lib/supabase';
import { adminApi } from '../services/adminApi';
import { FEATURE_CATALOG, type FeatureCatalogEntry } from '../lib/featureCatalog';
import type {
  TenantDirectoryRow, TenantConfigEntry, FeatureEntry,
  MergedUserEntry, UserOverrideEntry, SchedulerEntry,
} from '../types/admin';
import Modal from '../components/Modal';
import { fmtLocalTime } from '../lib/formatTime';

interface SystemAdminPageProps {
  onBack?: () => void;
}

type DetailTab = 'health' | 'config' | 'features' | 'users' | 'scheduler' | 'audit';

export default function SystemAdminPage({ onBack }: SystemAdminPageProps) {
  const { branding, hasRole } = useAuthContext();
  const isSystemAdmin = hasRole('GeniReports Admin');
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [tab, setTab] = useState<DetailTab>('health');

  const tenantsQuery = useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: async () => {
      if (!supabaseConfigured) throw new Error('Supabase env vars not set on this deploy');
      const { data, error } = await supabase
        .from('tenants')
        .select('*')
        .eq('app', 'genireports')
        .order('display_name');
      if (error) throw new Error(error.message);
      return (data ?? []) as TenantDirectoryRow[];
    },
    refetchOnWindowFocus: false,
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <div className="max-w-[1600px] mx-auto">
        <header className="border-b border-slate-800/50 backdrop-blur-sm bg-slate-950/50 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {onBack && (
                <button
                  onClick={onBack}
                  className="p-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all"
                >
                  <ArrowLeft size={18} />
                </button>
              )}
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-violet-600 flex items-center justify-center text-white shadow-lg shadow-violet-500/20">
                  <Shield size={18} />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-white tracking-tight">System Admin</h1>
                  <p className="text-xs text-slate-500">
                    {branding?.company_name || 'GeniReports'} — Multi-tenant management
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={() => tenantsQuery.refetch()}
              disabled={tenantsQuery.isFetching}
              className="p-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all disabled:opacity-50"
              title="Refresh tenants"
            >
              <RefreshCw size={16} className={tenantsQuery.isFetching ? 'animate-spin' : ''} />
            </button>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
          {/* Left: tenant list */}
          <aside className="bg-slate-900/40 border border-slate-800/50 rounded-2xl p-3 h-fit">
            <div className="flex items-center justify-between px-2 py-1.5 mb-2">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">Tenants</h2>
              <span className="text-xs text-slate-500">
                {tenantsQuery.data ? `${tenantsQuery.data.length}` : '…'}
              </span>
            </div>

            {tenantsQuery.isLoading && (
              <div className="px-3 py-6 text-sm text-slate-500 flex items-center gap-2">
                <RefreshCw size={14} className="animate-spin" /> Loading tenants...
              </div>
            )}

            {tenantsQuery.error && (
              <div className="px-3 py-3 mx-1 mb-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs flex items-start gap-2">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <div>{(tenantsQuery.error as Error).message}</div>
              </div>
            )}

            {tenantsQuery.data && tenantsQuery.data.length === 0 && (
              <div className="px-3 py-4 text-xs text-slate-500">
                No tenants. Add rows to Supabase <code className="text-slate-400">tenants</code> table.
              </div>
            )}

            <ul className="space-y-1">
              {tenantsQuery.data?.map((t) => (
                <li key={t.slug}>
                  <button
                    onClick={() => { setSelectedSlug(t.slug); setTab('health'); }}
                    className={`w-full text-left px-3 py-2.5 rounded-lg transition-all flex items-start justify-between gap-2 ${
                      selectedSlug === t.slug
                        ? 'bg-violet-500/15 border border-violet-500/30 text-white'
                        : 'border border-transparent text-slate-300 hover:bg-slate-800/50 hover:text-white'
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium flex items-center gap-2">
                        <Building2 size={13} className="text-slate-500 shrink-0" />
                        <span className="truncate">{t.display_name}</span>
                      </div>
                      <div className="text-[11px] text-slate-500 font-mono truncate mt-0.5">{t.slug}</div>
                    </div>
                    {t.active
                      ? <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 font-medium shrink-0">ON</span>
                      : <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 font-medium shrink-0">OFF</span>}
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          {/* Right: detail panel */}
          <section>
            {!selectedSlug && (
              <div className="bg-slate-900/40 border border-slate-800/50 rounded-2xl p-10 text-center text-slate-500">
                <Shield size={36} className="mx-auto mb-3 text-slate-700" />
                <p className="text-sm">Select a tenant on the left to view its admin store.</p>
              </div>
            )}

            {selectedSlug && (
              <>
                <TenantHeader
                  tenant={tenantsQuery.data?.find(t => t.slug === selectedSlug)}
                  canToggleStatus={isSystemAdmin}
                />

                <div className="flex items-center gap-1 border-b border-slate-700/40 mb-4">
                  {(['health', 'config', 'features', 'users', 'scheduler', 'audit'] as DetailTab[]).map((t) => (
                    <button
                      key={t}
                      onClick={() => setTab(t)}
                      className={`px-4 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px capitalize ${
                        tab === t
                          ? 'text-violet-300 border-violet-500'
                          : 'text-slate-500 border-transparent hover:text-slate-300'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>

                {tab === 'health'    && <HealthPanel    slug={selectedSlug} />}
                {tab === 'config'    && <ConfigPanel    slug={selectedSlug} />}
                {tab === 'features'  && <FeaturesPanel  slug={selectedSlug} />}
                {tab === 'users'     && <UsersPanel     slug={selectedSlug} />}
                {tab === 'scheduler' && <SchedulerPanel slug={selectedSlug} defaultTimeZone={tenantsQuery.data?.find(t => t.slug === selectedSlug)?.time_zone ?? null} />}
                {tab === 'audit'     && <AuditPanel     slug={selectedSlug} />}
              </>
            )}
          </section>
        </main>
      </div>
    </div>
  );
}

// ── Subcomponents ────────────────────────────────────────────────

function TenantHeader({
  tenant,
  canToggleStatus,
}: {
  tenant: TenantDirectoryRow | undefined;
  canToggleStatus: boolean;
}) {
  const [confirming, setConfirming] = useState(false);
  if (!tenant) return null;

  return (
    <div className="bg-slate-900/40 border border-slate-800/50 rounded-2xl p-5 mb-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-white">{tenant.display_name}</h2>
          <div className="text-xs text-slate-500 font-mono mt-1">{tenant.slug}</div>
          <a
            href={tenant.agent_url}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-violet-300 hover:text-violet-200 underline mt-2 inline-block"
          >
            {tenant.agent_url}
          </a>
        </div>
        <div className="text-right text-xs text-slate-500 space-y-1">
          <div>tz: <span className="text-slate-300">{tenant.time_zone}</span></div>
          {tenant.agent_min_version && (
            <div>min agent: <span className="text-slate-300 font-mono">{tenant.agent_min_version}</span></div>
          )}
          <div className="pt-1">
            {tenant.active ? (
              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 font-medium">
                <Power size={10} /> ACTIVE
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 font-medium">
                <PowerOff size={10} /> SUSPENDED
              </span>
            )}
          </div>
          {!tenant.active && tenant.suspension_reason && (
            <div className="text-amber-300/80 italic mt-1 max-w-[280px] text-right">
              {tenant.suspension_reason}
            </div>
          )}
          {canToggleStatus && (
            <button
              onClick={() => setConfirming(true)}
              className={`mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border transition-colors ${
                tenant.active
                  ? 'bg-amber-500/10 border-amber-500/30 text-amber-200 hover:bg-amber-500/20'
                  : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/20'
              }`}
            >
              {tenant.active ? <><PowerOff size={12} /> Suspend</> : <><Power size={12} /> Activate</>}
            </button>
          )}
        </div>
      </div>

      {confirming && (
        <TenantStatusModal
          tenant={tenant}
          onClose={() => setConfirming(false)}
        />
      )}
    </div>
  );
}

function TenantStatusModal({
  tenant,
  onClose,
}: {
  tenant: TenantDirectoryRow;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const turningOff = tenant.active;
  const [reason, setReason] = useState(tenant.suspension_reason ?? '');

  const mut = useMutation({
    mutationFn: () => adminApi.setTenantStatus(
      tenant.slug,
      !turningOff,
      turningOff ? reason.trim() : null,
    ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'tenants'] });
      qc.invalidateQueries({ queryKey: ['admin', 'audit', tenant.slug] });
      onClose();
    },
  });

  const canSave =
    !mut.isPending && (!turningOff || reason.trim().length > 0);

  return (
    <Modal
      onClose={onClose}
      title={turningOff ? `Suspend ${tenant.display_name}` : `Activate ${tenant.display_name}`}
    >
      <form
        onSubmit={(e) => { e.preventDefault(); if (canSave) mut.mutate(); }}
        className="space-y-4"
      >
        {turningOff ? (
          <>
            <p className="text-sm text-slate-300">
              Suspending will <span className="text-amber-200 font-semibold">block ALL admin API calls</span> for{' '}
              <span className="font-mono text-white">{tenant.slug}</span>. The agent will keep running, but the
              Netlify proxy will return 403 until the tenant is reactivated.
            </p>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">
                Suspension reason <span className="text-amber-300">(required)</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                required
                placeholder="e.g. Unpaid invoice — see ticket #1234"
                className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-md px-3 py-2"
              />
              <div className="text-[10px] text-slate-600 mt-1">
                Shown to anyone whose admin call is blocked.
              </div>
            </div>
          </>
        ) : (
          <p className="text-sm text-slate-300">
            Activating will restore admin API access for{' '}
            <span className="font-mono text-white">{tenant.slug}</span>. The existing suspension reason will be
            cleared.
          </p>
        )}

        {mut.error && <ErrorBox err={mut.error} />}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-700/40">
          <button
            type="button"
            onClick={onClose}
            disabled={mut.isPending}
            className="px-4 py-2 text-sm text-slate-300 hover:text-white rounded-md hover:bg-slate-800/60 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSave}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              turningOff
                ? 'bg-amber-500/20 border-amber-500/40 text-amber-100 hover:bg-amber-500/30'
                : 'bg-emerald-500/20 border-emerald-500/40 text-emerald-100 hover:bg-emerald-500/30'
            }`}
          >
            {mut.isPending
              ? <Loader2 size={14} className="animate-spin" />
              : turningOff
                ? <PowerOff size={14} />
                : <Power size={14} />}
            {turningOff ? 'Suspend' : 'Activate'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function PanelCard({ children }: { children: React.ReactNode }) {
  return <div className="bg-slate-900/40 border border-slate-800/50 rounded-2xl p-5">{children}</div>;
}

function ErrorBox({ err }: { err: unknown }) {
  return (
    <div className="rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm p-4 flex items-start gap-2">
      <AlertCircle size={16} className="mt-0.5 shrink-0" />
      <div>{err instanceof Error ? err.message : String(err)}</div>
    </div>
  );
}

function HealthPanel({ slug }: { slug: string }) {
  const q = useQuery({
    queryKey: ['admin', 'health', slug],
    queryFn: () => adminApi.getHealth(slug),
    refetchOnWindowFocus: false,
    retry: 0,
  });

  if (q.isLoading) return <PanelCard><div className="text-sm text-slate-500 flex items-center gap-2"><RefreshCw size={14} className="animate-spin" /> Loading...</div></PanelCard>;
  if (q.error)     return <PanelCard><ErrorBox err={q.error} /></PanelCard>;

  const d = q.data!;
  const cells: Array<[string, React.ReactNode]> = [
    ['admin.duckdb path', <span className="font-mono break-all">{d.filePath}</span>],
    ['File size', `${(d.fileSizeBytes / 1024).toFixed(1)} KB`],
    ['Schema version', d.schemaVersion ?? '—'],
    ['Last write', fmtLocalTime(d.lastWriteAt) || 'never'],
  ];

  return (
    <PanelCard>
      <div className="flex items-center gap-2 mb-4">
        <Server size={16} className="text-violet-300" />
        <h3 className="text-sm font-semibold text-white">Agent Admin Store</h3>
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
        {cells.map(([k, v]) => (
          <div key={k} className="text-sm">
            <dt className="text-xs text-slate-500 uppercase tracking-wider mb-0.5">{k}</dt>
            <dd className="text-slate-200">{v}</dd>
          </div>
        ))}
      </dl>
    </PanelCard>
  );
}

// ─── Config tab (editable) ────────────────────────────────────────

function ConfigPanel({ slug }: { slug: string }) {
  const qc = useQueryClient();
  const queryKey = ['admin', 'config', slug];
  const q = useQuery({ queryKey, queryFn: () => adminApi.getAllConfig(slug), refetchOnWindowFocus: false });

  const [editing, setEditing] = useState<TenantConfigEntry | null>(null);
  const [creating, setCreating] = useState(false);

  const closeAndRefresh = () => {
    setEditing(null);
    setCreating(false);
    qc.invalidateQueries({ queryKey });
    qc.invalidateQueries({ queryKey: ['admin', 'audit', slug] });
  };

  if (q.isLoading) return <PanelCard><div className="text-sm text-slate-500 flex items-center gap-2"><RefreshCw size={14} className="animate-spin" /> Loading...</div></PanelCard>;
  if (q.error)     return <PanelCard><ErrorBox err={q.error} /></PanelCard>;

  return (
    <PanelCard>
      <div className="flex items-center gap-2 mb-4">
        <Settings size={16} className="text-violet-300" />
        <h3 className="text-sm font-semibold text-white">Tenant Config</h3>
        <span className="text-xs text-slate-500 ml-auto">{q.data?.length ?? 0} entries</span>
        <button
          onClick={() => setCreating(true)}
          className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-violet-500/15 border border-violet-500/30 text-violet-200 hover:bg-violet-500/25 transition-colors"
        >
          <Plus size={12} /> Add
        </button>
      </div>

      {(!q.data || q.data.length === 0) && (
        <div className="text-sm text-slate-500 py-4">No config entries yet — click <em>Add</em> to create one.</div>
      )}

      {q.data && q.data.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 uppercase tracking-wider border-b border-slate-700/40">
            <tr>
              <th className="text-left py-2 font-medium">Key</th>
              <th className="text-left py-2 font-medium">Value</th>
              <th className="text-left py-2 font-medium">Type</th>
              <th className="text-left py-2 font-medium">Updated</th>
              <th className="text-left py-2 font-medium">By</th>
              <th className="text-right py-2 font-medium w-12"></th>
            </tr>
          </thead>
          <tbody>
            {q.data.map((c) => (
              <tr key={c.key} className="border-b border-slate-700/20">
                <td className="py-2 font-mono text-xs text-slate-200">{c.key}</td>
                <td className="py-2 text-slate-300 break-all">{c.value ?? <span className="text-slate-500 italic">null</span>}</td>
                <td className="py-2 text-xs text-slate-500">{c.valueType ?? '—'}</td>
                <td className="py-2 text-xs text-slate-500">{fmtLocalTime(c.updatedAt) || '—'}</td>
                <td className="py-2 text-xs text-slate-500">{c.updatedBy ?? '—'}</td>
                <td className="py-2 text-right">
                  <button
                    onClick={() => setEditing(c)}
                    className="p-1.5 rounded text-slate-500 hover:text-violet-300 hover:bg-slate-800/60 transition-colors"
                    title="Edit"
                  >
                    <Pencil size={13} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {(editing || creating) && (
        <ConfigEditModal
          slug={slug}
          initial={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={closeAndRefresh}
        />
      )}
    </PanelCard>
  );
}

function ConfigEditModal({
  slug,
  initial,
  onClose,
  onSaved,
}: {
  slug: string;
  initial: TenantConfigEntry | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = !initial;
  const [key, setKey] = useState(initial?.key ?? '');
  const [value, setValue] = useState(initial?.value ?? '');
  const [valueType, setValueType] = useState<string>(initial?.valueType ?? 'string');

  const mut = useMutation({
    mutationFn: () => adminApi.putConfig(key.trim(), value, valueType, slug),
    onSuccess: () => onSaved(),
  });

  const canSave = key.trim().length > 0 && !mut.isPending;

  return (
    <Modal onClose={onClose} title={isNew ? 'Add Config Key' : `Edit ${initial!.key}`}>
      <form
        onSubmit={(e) => { e.preventDefault(); if (canSave) mut.mutate(); }}
        className="space-y-4"
      >
        <div>
          <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Key</label>
          <input
            type="text"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            disabled={!isNew}
            required
            placeholder="e.g. branding.primary_color"
            className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-md px-3 py-2 font-mono disabled:opacity-60 disabled:cursor-not-allowed"
          />
          {!isNew && <div className="text-[10px] text-slate-600 mt-1">Key is immutable — delete + re-add to rename.</div>}
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Value</label>
          <textarea
            value={value ?? ''}
            onChange={(e) => setValue(e.target.value)}
            rows={3}
            placeholder={valueType === 'json' ? '{"foo":"bar"}' : ''}
            className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-md px-3 py-2 font-mono"
          />
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Type</label>
          <select
            value={valueType}
            onChange={(e) => setValueType(e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-md px-3 py-2"
          >
            <option value="string">string</option>
            <option value="int">int</option>
            <option value="bool">bool</option>
            <option value="json">json</option>
          </select>
        </div>

        {mut.error && <ErrorBox err={mut.error} />}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-700/40">
          <button
            type="button"
            onClick={onClose}
            disabled={mut.isPending}
            className="px-4 py-2 text-sm text-slate-300 hover:text-white rounded-md hover:bg-slate-800/60 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSave}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-violet-500/20 border border-violet-500/40 text-violet-100 hover:bg-violet-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {mut.isPending ? <Loader2 size={14} className="animate-spin" /> : null}
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Features tab (editable) ──────────────────────────────────────

function FeaturesPanel({ slug }: { slug: string }) {
  const qc = useQueryClient();
  const queryKey = ['admin', 'features', slug];
  const q = useQuery({ queryKey, queryFn: () => adminApi.getAllFeatures(slug), refetchOnWindowFocus: false });

  const [busyKey, setBusyKey] = useState<string | null>(null);

  const toggle = useMutation({
    mutationFn: ({ featureKey, isEnabled }: { featureKey: string; isEnabled: boolean }) =>
      adminApi.putFeature(featureKey, isEnabled, slug),
    onMutate: ({ featureKey }) => setBusyKey(featureKey),
    onSettled: () => {
      setBusyKey(null);
      qc.invalidateQueries({ queryKey });
      qc.invalidateQueries({ queryKey: ['admin', 'audit', slug] });
    },
  });

  if (q.isLoading) return <PanelCard><div className="text-sm text-slate-500 flex items-center gap-2"><RefreshCw size={14} className="animate-spin" /> Loading...</div></PanelCard>;
  if (q.error)     return <PanelCard><ErrorBox err={q.error} /></PanelCard>;

  // Catalog is the canonical list — drive UI from it, merge DB state in.
  // Catalog entries with sysadminOnly are skipped (they're not per-tenant
  // features). Any DB entries NOT in the catalog are shown at the bottom
  // as "unknown" so legacy ad-hoc keys are still visible/editable.
  const catalog = FEATURE_CATALOG.filter((f) => !f.sysadminOnly);
  const byKey = new Map((q.data ?? []).map((f) => [f.featureKey, f]));
  const orphans = (q.data ?? []).filter((f) => !catalog.some((c) => c.key === f.featureKey));
  const grouped = groupByCategory(catalog);

  return (
    <PanelCard>
      <div className="flex items-center gap-2 mb-3">
        <Activity size={16} className="text-violet-300" />
        <h3 className="text-sm font-semibold text-white">Tenant Features</h3>
        <span className="text-xs text-slate-500 ml-auto">{catalog.length} feature{catalog.length === 1 ? '' : 's'} in catalog</span>
      </div>

      <p className="text-xs text-slate-500 mb-4">
        Features are defined in code (<code className="text-slate-400">src/lib/featureCatalog.ts</code>).
        Toggling here sets this tenant's default; per-user overrides live under each user in the Users tab.
        Features without an explicit toggle here fall back to their catalog default.
      </p>

      {grouped.map(([category, items]) => (
        <div key={category} className="mb-5">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">{categoryLabel(category)}</div>
          <ul className="divide-y divide-slate-700/40 border border-slate-800/50 rounded-lg overflow-hidden">
            {items.map((cat) => {
              const dbEntry = byKey.get(cat.key);
              return (
                <CatalogFeatureRow
                  key={cat.key}
                  cat={cat}
                  dbEntry={dbEntry}
                  busy={busyKey === cat.key}
                  onToggle={(next) => toggle.mutate({ featureKey: cat.key, isEnabled: next })}
                />
              );
            })}
          </ul>
        </div>
      ))}

      {orphans.length > 0 && (
        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-wider text-amber-300/70 mb-2">Unknown keys (not in catalog)</div>
          <ul className="divide-y divide-slate-700/40 border border-amber-500/20 rounded-lg overflow-hidden">
            {orphans.map((f) => (
              <FeatureRow
                key={f.featureKey}
                feature={f}
                busy={busyKey === f.featureKey}
                onToggle={(next) => toggle.mutate({ featureKey: f.featureKey, isEnabled: next })}
              />
            ))}
          </ul>
        </div>
      )}

      {toggle.error && <div className="mt-3"><ErrorBox err={toggle.error} /></div>}
    </PanelCard>
  );
}

function groupByCategory(items: FeatureCatalogEntry[]): Array<[FeatureCatalogEntry['category'], FeatureCatalogEntry[]]> {
  const order: FeatureCatalogEntry['category'][] = ['reports', 'operations', 'integrations', 'account', 'admin'];
  const map = new Map<FeatureCatalogEntry['category'], FeatureCatalogEntry[]>();
  for (const i of items) {
    const arr = map.get(i.category) ?? [];
    arr.push(i);
    map.set(i.category, arr);
  }
  return order.filter((c) => map.has(c)).map((c) => [c, map.get(c)!]);
}

function categoryLabel(c: FeatureCatalogEntry['category']): string {
  return ({ reports: 'Reports', operations: 'Operations', integrations: 'Integrations', account: 'Account', admin: 'Admin' } as const)[c];
}

function CatalogFeatureRow({
  cat, dbEntry, busy, onToggle,
}: {
  cat: FeatureCatalogEntry;
  dbEntry: FeatureEntry | undefined;
  busy: boolean;
  onToggle: (next: boolean) => void;
}) {
  // Effective state for the toggle: if DB has it, use that; else catalog default.
  const isEnabled = dbEntry ? dbEntry.isEnabled : cat.defaultEnabled;
  return (
    <li className="py-3 px-3 flex items-center justify-between gap-4 bg-slate-900/30">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium text-slate-200">{cat.label}</div>
          <code className="text-[10px] text-slate-600 font-mono">{cat.key}</code>
          {!dbEntry && (
            <span className="text-[10px] px-1.5 py-0.5 rounded font-medium border bg-slate-700/40 text-slate-400 border-slate-600/40" title="No explicit DB row yet — using catalog default. Toggling will create the row.">
              default
            </span>
          )}
        </div>
        <div className="text-xs text-slate-500 mt-0.5">{cat.description}</div>
        {dbEntry && (
          <div className="text-[10px] text-slate-600 mt-0.5">
            {dbEntry.updatedBy ? `${dbEntry.updatedBy} · ` : ''}{fmtLocalTime(dbEntry.updatedAt)}
          </div>
        )}
      </div>
      <button
        onClick={() => onToggle(!isEnabled)}
        disabled={busy}
        className="disabled:opacity-50 shrink-0"
        title={isEnabled ? 'Disable for tenant' : 'Enable for tenant'}
      >
        {busy
          ? <Loader2 size={28} className="animate-spin text-slate-400" />
          : isEnabled
            ? <ToggleRight size={28} className="text-emerald-400 hover:text-emerald-300 transition-colors" />
            : <ToggleLeft size={28} className="text-slate-600 hover:text-slate-500 transition-colors" />}
      </button>
    </li>
  );
}

function FeatureRow({
  feature,
  busy,
  onToggle,
}: {
  feature: FeatureEntry;
  busy: boolean;
  onToggle: (next: boolean) => void;
}) {
  return (
    <li className="py-3 flex items-center justify-between gap-4">
      <div className="min-w-0">
        <div className="text-sm font-medium text-slate-200 font-mono">{feature.featureKey}</div>
        <div className="text-xs text-slate-500 mt-0.5">
          {feature.updatedBy ? `${feature.updatedBy} · ` : ''}{fmtLocalTime(feature.updatedAt)}
        </div>
      </div>
      <button
        onClick={() => onToggle(!feature.isEnabled)}
        disabled={busy}
        className="disabled:opacity-50"
        title={feature.isEnabled ? 'Disable for tenant' : 'Enable for tenant'}
      >
        {busy
          ? <Loader2 size={28} className="animate-spin text-slate-400" />
          : feature.isEnabled
            ? <ToggleRight size={28} className="text-emerald-400 hover:text-emerald-300 transition-colors" />
            : <ToggleLeft size={28} className="text-slate-600 hover:text-slate-500 transition-colors" />}
      </button>
    </li>
  );
}

// FeatureCreateModal removed in PR2 — the catalog (src/lib/featureCatalog.ts)
// is now the source of truth for which features exist. To add a feature,
// edit the catalog and the Features tab will pick it up automatically.

// ─── Users tab (invite / revoke / per-user overrides) ────────────

function UsersPanel({ slug }: { slug: string }) {
  const qc = useQueryClient();
  const usersKey = ['admin', 'members', slug];
  const featuresKey = ['admin', 'features', slug];
  // PR1: listMembers merges Auth0 users (app_metadata.tenants[*].slug = slug)
  // with the agent's additional_users table. Catches users created via the
  // legacy admin site or directly in Auth0 that have no agent row yet.
  const usersQ = useQuery({
    queryKey: usersKey,
    queryFn: () => adminApi.listMembers(slug),
    refetchOnWindowFocus: false,
  });

  const [inviting, setInviting] = useState(false);
  const [editingUser, setEditingUser] = useState<MergedUserEntry | null>(null);
  const [overridingUser, setOverridingUser] = useState<MergedUserEntry | null>(null);
  const [revoking, setRevoking] = useState<MergedUserEntry | null>(null);

  const revokeMut = useMutation({
    mutationFn: (sub: string) => adminApi.revokeUser(sub, slug),
    onSettled: () => {
      setRevoking(null);
      qc.invalidateQueries({ queryKey: usersKey });
      qc.invalidateQueries({ queryKey: ['admin', 'audit', slug] });
    },
  });

  if (usersQ.isLoading) return <PanelCard><div className="text-sm text-slate-500 flex items-center gap-2"><RefreshCw size={14} className="animate-spin" /> Loading...</div></PanelCard>;
  if (usersQ.error)     return <PanelCard><ErrorBox err={usersQ.error} /></PanelCard>;

  const users = usersQ.data?.users ?? [];
  const counts = usersQ.data?.counts;
  const warnings = usersQ.data?.warnings ?? [];

  return (
    <PanelCard>
      <div className="flex items-center gap-2 mb-2">
        <UsersIcon size={16} className="text-violet-300" />
        <h3 className="text-sm font-semibold text-white">Members</h3>
        <span className="text-xs text-slate-500 ml-auto">
          {users.length} member{users.length === 1 ? '' : 's'}
          {counts && counts.auth0Only > 0 && (
            <span className="ml-2 text-amber-300/70" title={`${counts.auth0Only} member(s) without an agent row (legacy/auth0-only)`}>
              · {counts.auth0Only} unprovisioned
            </span>
          )}
        </span>
        <button
          onClick={() => setInviting(true)}
          className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-violet-500/15 border border-violet-500/30 text-violet-200 hover:bg-violet-500/25 transition-colors"
        >
          <Plus size={12} /> Invite
        </button>
      </div>

      {warnings.length > 0 && (
        <div className="mb-3 rounded-lg bg-amber-500/10 border border-amber-500/30 p-2 text-xs text-amber-200">
          {warnings.map((w, i) => <div key={i}>{w}</div>)}
        </div>
      )}

      {users.length === 0 && (
        <div className="text-sm text-slate-500 py-4">No members for this tenant yet — click <em>Invite</em> to add one.</div>
      )}

      {users.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 uppercase tracking-wider border-b border-slate-700/40">
            <tr>
              <th className="text-left py-2 font-medium">User</th>
              <th className="text-left py-2 font-medium">Role</th>
              <th className="text-left py-2 font-medium">Source</th>
              <th className="text-left py-2 font-medium">Last login</th>
              <th className="text-right py-2 font-medium w-24"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.auth0Sub} className={`border-b border-slate-700/20 ${u.blocked ? 'opacity-60' : ''}`}>
                <td className="py-2">
                  <div className="flex items-center gap-2">
                    {u.picture && <img src={u.picture} alt="" className="w-6 h-6 rounded-full border border-slate-700" />}
                    <div>
                      <div className="text-slate-100 font-semibold">
                        {u.name || u.email}
                        {u.blocked && <span className="ml-2 text-[10px] px-1 py-0.5 rounded bg-red-500/15 text-red-300 border border-red-500/30">BLOCKED</span>}
                        {!u.emailVerified && u.source !== 'agent' && <span className="ml-2 text-[10px] px-1 py-0.5 rounded bg-slate-700/40 text-slate-400 border border-slate-600/40" title="Email not yet verified">UNVERIFIED</span>}
                      </div>
                      {u.name && <div className="text-xs text-slate-400">{u.email}</div>}
                      <div className="text-[10px] text-slate-600 font-mono">{u.auth0Sub}</div>
                    </div>
                  </div>
                </td>
                <td className="py-2">
                  {u.role ? (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium border ${
                      u.role === 'account_admin'
                        ? 'bg-violet-500/15 text-violet-300 border-violet-500/30'
                        : 'bg-slate-700/40 text-slate-300 border-slate-600/40'
                    }`}>
                      {u.role}
                    </span>
                  ) : (
                    <span className="text-[10px] px-1.5 py-0.5 rounded font-medium border bg-amber-500/10 text-amber-300 border-amber-500/30" title="No additional_users row on the agent yet">
                      no agent row
                    </span>
                  )}
                </td>
                <td className="py-2">
                  <SourceChip source={u.source} />
                </td>
                <td className="py-2 text-xs text-slate-500">
                  {u.lastLogin
                    ? fmtLocalTime(u.lastLogin)
                    : u.lastSeen
                      ? fmtLocalTime(u.lastSeen)
                      : <span className="italic text-slate-600">never</span>}
                  {u.loginsCount != null && u.loginsCount > 0 && (
                    <div className="text-[10px] text-slate-600">{u.loginsCount} login{u.loginsCount === 1 ? '' : 's'}</div>
                  )}
                </td>
                <td className="py-2 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button
                      onClick={() => setEditingUser(u)}
                      className="p-1.5 rounded text-slate-500 hover:text-sky-300 hover:bg-slate-800/60 transition-colors"
                      title="Edit name + Auth0 role"
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      onClick={() => setOverridingUser(u)}
                      className="p-1.5 rounded text-slate-500 hover:text-violet-300 hover:bg-slate-800/60 transition-colors"
                      title="Per-user feature overrides"
                    >
                      <SlidersHorizontal size={13} />
                    </button>
                    <button
                      onClick={() => setRevoking(u)}
                      disabled={u.source === 'auth0'}
                      className="p-1.5 rounded text-slate-500 hover:text-red-300 hover:bg-slate-800/60 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      title={u.source === 'auth0'
                        ? 'No agent row to revoke. Use Auth0 dashboard to remove tenant access for this legacy user.'
                        : 'Revoke user'}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {revokeMut.error && <div className="mt-3"><ErrorBox err={revokeMut.error} /></div>}

      {inviting && (
        <InviteUserModal
          slug={slug}
          onClose={() => setInviting(false)}
          onInvited={() => {
            setInviting(false);
            qc.invalidateQueries({ queryKey: usersKey });
            qc.invalidateQueries({ queryKey: ['admin', 'audit', slug] });
          }}
        />
      )}

      {editingUser && (
        <EditUserModal
          slug={slug}
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSaved={() => {
            setEditingUser(null);
            qc.invalidateQueries({ queryKey: usersKey });
          }}
        />
      )}

      {overridingUser && (
        <UserOverridesModal
          slug={slug}
          user={overridingUser}
          featuresKey={featuresKey}
          onClose={() => setOverridingUser(null)}
        />
      )}

      {revoking && (
        <Modal onClose={() => setRevoking(null)} title="Revoke user">
          <div className="space-y-4">
            <p className="text-sm text-slate-300">
              Revoke <span className="font-semibold text-white">{revoking.email}</span>?
              They will lose access to this tenant and their feature overrides will be deleted.
            </p>
            {revokeMut.error && <ErrorBox err={revokeMut.error} />}
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-700/40">
              <button
                onClick={() => setRevoking(null)}
                disabled={revokeMut.isPending}
                className="px-4 py-2 text-sm text-slate-300 hover:text-white rounded-md hover:bg-slate-800/60 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => revokeMut.mutate(revoking.auth0Sub)}
                disabled={revokeMut.isPending}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-red-500/20 border border-red-500/40 text-red-100 hover:bg-red-500/30 transition-colors disabled:opacity-50"
              >
                {revokeMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                Revoke
              </button>
            </div>
          </div>
        </Modal>
      )}
    </PanelCard>
  );
}

function InviteUserModal({
  slug,
  onClose,
  onInvited,
}: {
  slug: string;
  onClose: () => void;
  onInvited: () => void;
}) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [tenantRole, setTenantRole] = useState<'user' | 'account_admin'>('user');
  const [auth0Role, setAuth0Role] = useState<string>('DellDataReports');
  const [result, setResult] = useState<null | {
    auth0Sub: string;
    ticketUrl: string | null;
    userExisted: boolean;
    assignedRole: string | null;
    emailSent: boolean;
    emailError: string | null;
    agentInsertWarning: string | null;
  }>(null);

  const mut = useMutation({
    mutationFn: () => adminApi.createUserInvite({
      email: email.trim(),
      firstName: firstName.trim() || undefined,
      lastName:  lastName.trim()  || undefined,
      tenantRole,
      auth0Role: auth0Role.trim() || undefined,
    }, slug),
    onSuccess: (r) => setResult({
      auth0Sub: r.auth0Sub,
      ticketUrl: r.ticketUrl,
      userExisted: r.userExisted,
      assignedRole: r.assignedRole,
      emailSent: r.emailSent,
      emailError: r.emailError,
      agentInsertWarning: r.agentInsertWarning,
    }),
  });

  const canSave = email.trim().length > 0 && email.includes('@') && !mut.isPending;

  // Success view — show the ticket URL so the admin can copy it manually
  if (result) {
    const link = result.ticketUrl;
    return (
      <Modal onClose={() => { onInvited(); }} title="Invite sent">
        <div className="space-y-4 text-sm">
          <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-3 text-emerald-200">
            {result.userExisted
              ? <>This email already existed in Auth0 — tenant access was added to their account.</>
              : <>New Auth0 user created and a password-set link was generated.</>}
          </div>
          <dl className="grid grid-cols-[100px_1fr] gap-y-2 gap-x-3 text-xs">
            <dt className="text-slate-500 uppercase tracking-wider">Email</dt><dd className="text-slate-200">{email}</dd>
            <dt className="text-slate-500 uppercase tracking-wider">Auth0 sub</dt><dd className="text-slate-200 font-mono break-all">{result.auth0Sub}</dd>
            <dt className="text-slate-500 uppercase tracking-wider">Tenant role</dt><dd className="text-slate-200">{tenantRole}</dd>
            {result.assignedRole && (
              <>
                <dt className="text-slate-500 uppercase tracking-wider">Auth0 role</dt>
                <dd className="text-slate-200">{result.assignedRole}</dd>
              </>
            )}
          </dl>

          <div className={`rounded-lg p-3 text-sm border ${
            result.emailSent
              ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200'
              : 'bg-amber-500/10 border-amber-500/30 text-amber-200'
          }`}>
            {result.emailSent
              ? <>✉️ Password-reset email sent to <span className="font-mono">{email}</span>. They should check inbox (and spam).</>
              : <>⚠️ Auth0 did NOT send the password-reset email{result.emailError ? <> ({result.emailError})</> : ''}. Share the link below manually.</>}
          </div>

          {link ? (
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Password-set link (7 days)</div>
              <div className="rounded-lg bg-slate-800/60 border border-slate-700/40 p-3 break-all text-xs font-mono text-violet-200 select-all">
                {link}
              </div>
              <div className="text-[10px] text-slate-500 mt-1.5">
                Use this if the email above didn't arrive — just paste in a chat or your own email.
              </div>
            </div>
          ) : (
            <div className="text-xs text-slate-500 italic">
              Password ticket creation skipped. The user will need a manual password reset from the Auth0 dashboard.
            </div>
          )}

          {result.agentInsertWarning && (
            <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 text-amber-200 text-xs">
              ⚠️ Agent additional_users insert had a warning: {result.agentInsertWarning}
            </div>
          )}

          <div className="flex justify-end pt-2 border-t border-slate-700/40">
            <button
              onClick={onInvited}
              className="px-4 py-2 text-sm font-medium rounded-md bg-violet-500/20 border border-violet-500/40 text-violet-100 hover:bg-violet-500/30 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} title="Invite User">
      <form
        onSubmit={(e) => { e.preventDefault(); if (canSave) mut.mutate(); }}
        className="space-y-4"
      >
        <div>
          <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="user@company.com"
            className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-md px-3 py-2"
          />
          <div className="text-[10px] text-slate-600 mt-1">
            The Auth0 user is created or matched automatically. They get a password-set email.
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">First name</label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Jane"
              className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-md px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Last name</label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Doe"
              className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-md px-3 py-2"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Tenant role</label>
            <select
              value={tenantRole}
              onChange={(e) => setTenantRole(e.target.value as 'user' | 'account_admin')}
              className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-md px-3 py-2"
            >
              <option value="user">user</option>
              <option value="account_admin">account_admin</option>
            </select>
            <div className="text-[10px] text-slate-600 mt-1">
              In <code>additional_users</code> on the agent.
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Auth0 role (optional)</label>
            <input
              type="text"
              list="auth0-role-suggestions"
              value={auth0Role}
              onChange={(e) => setAuth0Role(e.target.value)}
              placeholder="e.g. DellDataReports"
              className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-md px-3 py-2 font-mono"
            />
            <datalist id="auth0-role-suggestions">
              <option value="DellDataReports" />
              <option value="GeniReports Admin" />
              <option value="QBO User" />
              <option value="Devash Loading" />
            </datalist>
            <div className="text-[10px] text-slate-600 mt-1">
              Drives which landing tiles they see. Leave blank for tenant-only access.
            </div>
          </div>
        </div>

        {mut.error && <ErrorBox err={mut.error} />}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-700/40">
          <button
            type="button"
            onClick={onClose}
            disabled={mut.isPending}
            className="px-4 py-2 text-sm text-slate-300 hover:text-white rounded-md hover:bg-slate-800/60 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSave}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-violet-500/20 border border-violet-500/40 text-violet-100 hover:bg-violet-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {mut.isPending ? <Loader2 size={14} className="animate-spin" /> : null}
            Send invite
          </button>
        </div>
      </form>
    </Modal>
  );
}

function EditUserModal({
  slug,
  user,
  onClose,
  onSaved,
}: {
  slug: string;
  user: MergedUserEntry;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Pre-fill from what we already have on the merged row. Auth0 doesn't
  // expose given_name/family_name on the list endpoint we use, so first/
  // last default empty and the admin types whatever they want. The "name"
  // field is the Auth0 display name, often "First Last" — surface it so
  // they know what's currently set.
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [auth0Role, setAuth0Role] = useState<string>(''); // empty = leave unchanged
  // Tenant role pre-filled from current. Sending the same value as the
  // existing one is a harmless no-op upsert on the agent (writes a
  // user.update audit entry), so we just always send whatever's selected.
  const initialTenantRole = (user.role === 'account_admin' ? 'account_admin' : 'user');
  const [tenantRole, setTenantRole] = useState<'user' | 'account_admin'>(initialTenantRole);

  const mut = useMutation({
    mutationFn: () => adminApi.updateUser({
      auth0Sub: user.auth0Sub,
      firstName,
      lastName,
      auth0Role,
      // Only send tenantRole when it actually changed — avoids writing an
      // unnecessary user.update audit entry when admin saved name only.
      tenantRole: tenantRole !== initialTenantRole ? tenantRole : undefined,
      // Email required by the agent's upsert. Use whatever's on the row.
      email: user.email,
    }, slug),
    onSuccess: () => onSaved(),
  });

  return (
    <Modal onClose={onClose} title={`Edit — ${user.email}`}>
      <form
        onSubmit={(e) => { e.preventDefault(); if (!mut.isPending) mut.mutate(); }}
        className="space-y-4"
      >
        <div className="text-xs text-slate-500">
          Current Auth0 display name: <span className="text-slate-300">{user.name ?? <em className="text-slate-500">none</em>}</span>
        </div>
        <div className="text-[10px] text-slate-600">
          Empty fields mean <em>leave alone</em>. Only fields you fill in will be updated.
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">First name</label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Jane"
              className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-md px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Last name</label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Doe"
              className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-md px-3 py-2"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Tenant role</label>
            <select
              value={tenantRole}
              onChange={(e) => setTenantRole(e.target.value as 'user' | 'account_admin')}
              className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-md px-3 py-2"
            >
              <option value="user">user</option>
              <option value="account_admin">account_admin</option>
            </select>
            <div className="text-[10px] text-slate-600 mt-1">
              <code>additional_users.role</code> on the agent. account_admin sees all
              tenant tickets in Support.
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Auth0 role</label>
            <input
              type="text"
              list="auth0-role-suggestions-edit"
              value={auth0Role}
              onChange={(e) => setAuth0Role(e.target.value)}
              placeholder="DellDataReports"
              className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-md px-3 py-2 font-mono"
            />
            <datalist id="auth0-role-suggestions-edit">
              <option value="DellDataReports" />
              <option value="GeniReports Admin" />
              <option value="QBO User" />
              <option value="Devash Loading" />
            </datalist>
            <div className="text-[10px] text-slate-600 mt-1">
              Type to set; empty = leave alone. To clear, use the Auth0 dashboard.
            </div>
          </div>
        </div>

        {mut.error && <ErrorBox err={mut.error} />}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-700/40">
          <button
            type="button"
            onClick={onClose}
            disabled={mut.isPending}
            className="px-4 py-2 text-sm text-slate-300 hover:text-white rounded-md hover:bg-slate-800/60 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={mut.isPending}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-sky-500/20 border border-sky-500/40 text-sky-100 hover:bg-sky-500/30 transition-colors disabled:opacity-50"
          >
            {mut.isPending ? <Loader2 size={14} className="animate-spin" /> : null}
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
}

function UserOverridesModal({
  slug,
  user,
  featuresKey,
  onClose,
}: {
  slug: string;
  user: { auth0Sub: string; email: string };
  featuresKey: readonly unknown[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const overridesKey = ['admin', 'user-overrides', slug, user.auth0Sub];

  const featuresQ = useQuery({
    queryKey: featuresKey,
    queryFn: () => adminApi.getAllFeatures(slug),
    refetchOnWindowFocus: false,
  });

  const overridesQ = useQuery({
    queryKey: overridesKey,
    queryFn: () => adminApi.getAllUserOverrides(user.auth0Sub, slug),
    refetchOnWindowFocus: false,
  });

  const [busyKey, setBusyKey] = useState<string | null>(null);

  const overrideByKey = new Map<string, UserOverrideEntry>(
    (overridesQ.data ?? []).map((o) => [o.featureKey, o]),
  );

  const refreshAfterWrite = () => {
    setBusyKey(null);
    qc.invalidateQueries({ queryKey: overridesKey });
    qc.invalidateQueries({ queryKey: ['admin', 'audit', slug] });
  };

  const setMut = useMutation({
    mutationFn: ({ featureKey, isEnabled }: { featureKey: string; isEnabled: boolean }) =>
      adminApi.putUserOverride(user.auth0Sub, featureKey, isEnabled, slug),
    onMutate: ({ featureKey }) => setBusyKey(featureKey),
    onSettled: refreshAfterWrite,
  });

  const clearMut = useMutation({
    mutationFn: (featureKey: string) =>
      adminApi.deleteUserOverride(user.auth0Sub, featureKey, slug),
    onMutate: (featureKey) => setBusyKey(featureKey),
    onSettled: refreshAfterWrite,
  });

  const handleChange = (featureKey: string, next: 'default' | 'on' | 'off') => {
    if (next === 'default') clearMut.mutate(featureKey);
    else                    setMut.mutate({ featureKey, isEnabled: next === 'on' });
  };

  // Build the row list from the CATALOG (not just DB rows). Catalog is the
  // canonical "what features exist" — every entry shows up even if no
  // tenant default has been written yet, so admins can flip per-user
  // overrides on a feature that's still using its catalog default.
  // Any DB-only "orphans" (legacy ad-hoc keys not in catalog) are still
  // shown at the bottom so they remain editable.
  const dbByKey = new Map((featuresQ.data ?? []).map((f) => [f.featureKey, f]));
  const catalogRows = FEATURE_CATALOG
    .filter((c) => !c.sysadminOnly)
    .map((c) => ({
      featureKey: c.key,
      label: c.label,
      tenantDefault: dbByKey.get(c.key)?.isEnabled ?? c.defaultEnabled,
      hasDbRow: dbByKey.has(c.key),
    }));
  const orphanRows = (featuresQ.data ?? [])
    .filter((f) => !FEATURE_CATALOG.some((c) => c.key === f.featureKey))
    .map((f) => ({
      featureKey: f.featureKey,
      label: f.featureKey,
      tenantDefault: f.isEnabled,
      hasDbRow: true,
    }));
  const rows = [...catalogRows, ...orphanRows];

  const loading = featuresQ.isLoading || overridesQ.isLoading;
  const error   = featuresQ.error || overridesQ.error;
  const mutError = setMut.error || clearMut.error;

  return (
    <Modal onClose={onClose} title={`Overrides — ${user.email}`} panelClassName="max-w-2xl">
      <div className="space-y-4">
        <p className="text-xs text-slate-500">
          Per-user feature overrides win over the tenant default. <em>Default</em> means the user
          inherits the tenant setting; <em>Force on/off</em> pins the value for this user only.
        </p>

        {loading && (
          <div className="text-sm text-slate-500 flex items-center gap-2 py-4">
            <RefreshCw size={14} className="animate-spin" /> Loading...
          </div>
        )}

        {error && <ErrorBox err={error} />}

        {!loading && !error && rows.length === 0 && (
          <div className="text-sm text-slate-500 py-4">
            No features in the catalog — edit <code>src/lib/featureCatalog.ts</code> to add some.
          </div>
        )}

        {!loading && !error && rows.length > 0 && (
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 uppercase tracking-wider border-b border-slate-700/40">
              <tr>
                <th className="text-left py-2 font-medium">Feature</th>
                <th className="text-left py-2 font-medium w-24">Tenant</th>
                <th className="text-left py-2 font-medium w-56">Per-user</th>
                <th className="text-left py-2 font-medium w-20">Effective</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const ov = overrideByKey.get(row.featureKey);
                const mode: 'default' | 'on' | 'off' =
                  !ov ? 'default' : ov.isEnabled ? 'on' : 'off';
                const effective = ov ? ov.isEnabled : row.tenantDefault;
                const busy = busyKey === row.featureKey;
                return (
                  <tr key={row.featureKey} className="border-b border-slate-700/20">
                    <td className="py-2">
                      <div className="text-slate-200">{row.label}</div>
                      <div className="text-[10px] font-mono text-slate-600">{row.featureKey}</div>
                    </td>
                    <td className="py-2">
                      <Pill on={row.tenantDefault} muted />
                      {!row.hasDbRow && (
                        <div className="text-[9px] uppercase tracking-wider text-slate-600 mt-0.5">catalog default</div>
                      )}
                    </td>
                    <td className="py-2">
                      <div className="inline-flex rounded-md border border-slate-700 overflow-hidden">
                        {(['default', 'on', 'off'] as const).map((opt) => (
                          <button
                            key={opt}
                            disabled={busy || mode === opt}
                            onClick={() => handleChange(row.featureKey, opt)}
                            className={`px-2.5 py-1 text-[11px] uppercase tracking-wider transition-colors disabled:cursor-not-allowed ${
                              mode === opt
                                ? opt === 'default'
                                  ? 'bg-slate-700 text-white'
                                  : opt === 'on'
                                    ? 'bg-emerald-500/30 text-emerald-100'
                                    : 'bg-red-500/30 text-red-100'
                                : 'bg-slate-800/60 text-slate-400 hover:bg-slate-700/60 hover:text-slate-200'
                            }`}
                          >
                            {busy && mode !== opt
                              ? <span className="opacity-60">{opt}</span>
                              : opt}
                          </button>
                        ))}
                      </div>
                    </td>
                    <td className="py-2">
                      {busy ? <Loader2 size={14} className="animate-spin text-slate-500" /> : <Pill on={effective} />}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {mutError && <ErrorBox err={mutError} />}

        <div className="flex items-center justify-end pt-2 border-t border-slate-700/40">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-300 hover:text-white rounded-md hover:bg-slate-800/60 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
}

function SourceChip({ source }: { source: 'auth0' | 'agent' | 'both' }) {
  const map: Record<string, { label: string; cls: string; title: string }> = {
    both:  { label: 'Auth0 + agent', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', title: 'Member in Auth0 AND has an additional_users row on the agent.' },
    auth0: { label: 'Auth0 only',    cls: 'bg-amber-500/10 text-amber-300 border-amber-500/30',       title: 'Member in Auth0 but no row in the agent\'s additional_users yet. Likely created via legacy admin or Auth0 dashboard.' },
    agent: { label: 'Agent only',    cls: 'bg-red-500/10 text-red-300 border-red-500/30',             title: 'additional_users row exists on the agent but no matching Auth0 user found. Possibly stale.' },
  };
  const m = map[source] ?? map.auth0;
  return <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium border ${m.cls}`} title={m.title}>{m.label}</span>;
}

function Pill({ on, muted = false }: { on: boolean; muted?: boolean }) {
  if (on) {
    return (
      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium border ${
        muted
          ? 'bg-emerald-500/10 text-emerald-300/80 border-emerald-500/20'
          : 'bg-emerald-500/20 text-emerald-200 border-emerald-500/40'
      }`}>ON</span>
    );
  }
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium border ${
      muted
        ? 'bg-slate-700/30 text-slate-400 border-slate-600/30'
        : 'bg-slate-700/60 text-slate-300 border-slate-600/40'
    }`}>OFF</span>
  );
}

// ─── Scheduler tab (CRUD) ─────────────────────────────────────────

const DAY_LETTERS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'] as const;
const DAY_NAMES   = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
const SEND_TYPES  = ['load_sheet', 'route_sheets'] as const;

function parseDaysMask(mask: string | null): boolean[] {
  // 'MTWTFSS' = all 7 days. '-' (or any non-letter at position i) = day i off.
  // Comma list fallback ('Mon,Tue,...') is also tolerated.
  const out = [false, false, false, false, false, false, false];
  if (!mask) return out;
  if (mask.includes(',')) {
    const tokens = mask.split(',').map(s => s.trim().toLowerCase());
    DAY_NAMES.forEach((name, i) => { if (tokens.includes(name.toLowerCase())) out[i] = true; });
    return out;
  }
  for (let i = 0; i < Math.min(7, mask.length); i++) {
    const ch = mask[i];
    if (ch && ch !== '-' && ch.toUpperCase() === DAY_LETTERS[i]) out[i] = true;
  }
  return out;
}

function serializeDaysMask(days: boolean[]): string {
  return days.map((on, i) => (on ? DAY_LETTERS[i] : '-')).join('');
}

function formatTimeForInput(time: string | null): string {
  // Agent stores '14:30:00'; <input type="time"> wants 'HH:MM'.
  if (!time) return '';
  const m = /^(\d{2}:\d{2})/.exec(time);
  return m ? m[1] : '';
}

function SchedulerPanel({
  slug,
  defaultTimeZone,
}: {
  slug: string;
  defaultTimeZone: string | null;
}) {
  const qc = useQueryClient();
  const queryKey = ['admin', 'scheduler', slug];
  const q = useQuery({ queryKey, queryFn: () => adminApi.getAllScheduler(slug), refetchOnWindowFocus: false });

  const [editing, setEditing] = useState<SchedulerEntry | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<SchedulerEntry | null>(null);

  const refresh = () => {
    qc.invalidateQueries({ queryKey });
    qc.invalidateQueries({ queryKey: ['admin', 'audit', slug] });
  };

  const deleteMut = useMutation({
    mutationFn: (eventId: string) => adminApi.deleteScheduler(eventId, slug),
    onSettled: () => { setDeleting(null); refresh(); },
  });

  if (q.isLoading) return <PanelCard><div className="text-sm text-slate-500 flex items-center gap-2"><RefreshCw size={14} className="animate-spin" /> Loading...</div></PanelCard>;
  if (q.error)     return <PanelCard><ErrorBox err={q.error} /></PanelCard>;

  return (
    <PanelCard>
      <div className="flex items-center gap-2 mb-4">
        <Calendar size={16} className="text-violet-300" />
        <h3 className="text-sm font-semibold text-white">Scheduler</h3>
        <span className="text-xs text-slate-500 ml-auto">{q.data?.length ?? 0} event{q.data?.length === 1 ? '' : 's'}</span>
        <button
          onClick={() => setCreating(true)}
          className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-violet-500/15 border border-violet-500/30 text-violet-200 hover:bg-violet-500/25 transition-colors"
        >
          <Plus size={12} /> Add
        </button>
      </div>

      {(!q.data || q.data.length === 0) && (
        <div className="text-sm text-slate-500 py-4">
          No scheduler events — click <em>Add</em> to create one.
        </div>
      )}

      {q.data && q.data.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 uppercase tracking-wider border-b border-slate-700/40">
            <tr>
              <th className="text-left py-2 font-medium">Event ID</th>
              <th className="text-left py-2 font-medium">Send Type</th>
              <th className="text-left py-2 font-medium">Time</th>
              <th className="text-left py-2 font-medium">Days</th>
              <th className="text-left py-2 font-medium">TZ</th>
              <th className="text-left py-2 font-medium">Active</th>
              <th className="text-left py-2 font-medium">Last Run</th>
              <th className="text-right py-2 font-medium w-16"></th>
            </tr>
          </thead>
          <tbody>
            {q.data.map((s) => {
              const days = parseDaysMask(s.daysOfWeek);
              return (
                <tr key={s.eventId} className="border-b border-slate-700/20">
                  <td className="py-2 font-mono text-xs text-slate-200 break-all">{s.eventId}</td>
                  <td className="py-2">
                    <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-slate-800/60 text-slate-300 border border-slate-700/40">
                      {s.sendType}
                    </span>
                  </td>
                  <td className="py-2 text-slate-300 font-mono text-xs">{s.timeOfDay ?? '—'}</td>
                  <td className="py-2">
                    <div className="inline-flex gap-0.5 font-mono text-[10px]">
                      {DAY_LETTERS.map((letter, i) => (
                        <span
                          key={i}
                          className={`w-4 h-4 inline-flex items-center justify-center rounded ${
                            days[i]
                              ? 'bg-violet-500/30 text-violet-100 border border-violet-500/40'
                              : 'bg-slate-800/40 text-slate-600 border border-slate-700/40'
                          }`}
                          title={DAY_NAMES[i]}
                        >
                          {letter}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-2 text-xs text-slate-500 font-mono">{s.timeZone ?? '—'}</td>
                  <td className="py-2">
                    {s.active
                      ? <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">ON</span>
                      : <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-slate-700/40 text-slate-400 border border-slate-600/40">OFF</span>}
                  </td>
                  <td className="py-2 text-xs text-slate-500">{fmtLocalTime(s.lastRunAt) || <span className="italic text-slate-600">never</span>}</td>
                  <td className="py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => setEditing(s)}
                        className="p-1.5 rounded text-slate-500 hover:text-violet-300 hover:bg-slate-800/60 transition-colors"
                        title="Edit"
                      >
                        <Pencil size={13} />
                      </button>
                      <button
                        onClick={() => setDeleting(s)}
                        className="p-1.5 rounded text-slate-500 hover:text-red-300 hover:bg-slate-800/60 transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {(editing || creating) && (
        <SchedulerEditModal
          slug={slug}
          initial={editing}
          defaultTimeZone={defaultTimeZone}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); refresh(); }}
        />
      )}

      {deleting && (
        <Modal onClose={() => setDeleting(null)} title="Delete scheduler event">
          <div className="space-y-4">
            <p className="text-sm text-slate-300">
              Delete <span className="font-mono text-white">{deleting.eventId}</span>?
              The agent will stop firing this scheduled send.
            </p>
            {deleteMut.error && <ErrorBox err={deleteMut.error} />}
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-700/40">
              <button
                onClick={() => setDeleting(null)}
                disabled={deleteMut.isPending}
                className="px-4 py-2 text-sm text-slate-300 hover:text-white rounded-md hover:bg-slate-800/60 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMut.mutate(deleting.eventId)}
                disabled={deleteMut.isPending}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-red-500/20 border border-red-500/40 text-red-100 hover:bg-red-500/30 transition-colors disabled:opacity-50"
              >
                {deleteMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                Delete
              </button>
            </div>
          </div>
        </Modal>
      )}
    </PanelCard>
  );
}

function SchedulerEditModal({
  slug,
  initial,
  defaultTimeZone,
  onClose,
  onSaved,
}: {
  slug: string;
  initial: SchedulerEntry | null;
  defaultTimeZone: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = !initial;
  const [eventId, setEventId]   = useState(initial?.eventId ?? '');
  const [sendType, setSendType] = useState<string>(initial?.sendType ?? 'load_sheet');
  const [timeHM, setTimeHM]     = useState(formatTimeForInput(initial?.timeOfDay ?? null));
  const [days, setDays]         = useState<boolean[]>(parseDaysMask(initial?.daysOfWeek ?? null));
  const [timeZone, setTimeZone] = useState(initial?.timeZone ?? defaultTimeZone ?? '');
  const [active, setActive]     = useState(initial?.active ?? true);

  const mut = useMutation({
    mutationFn: () => adminApi.upsertScheduler(eventId.trim(), {
      sendType: sendType.trim(),
      timeOfDay: timeHM ? `${timeHM}:00` : null,
      daysOfWeek: days.some(Boolean) ? serializeDaysMask(days) : null,
      timeZone: timeZone.trim() || null,
      active,
    }, slug),
    onSuccess: () => onSaved(),
  });

  const canSave =
    eventId.trim().length > 0 &&
    sendType.trim().length > 0 &&
    !mut.isPending;

  return (
    <Modal
      onClose={onClose}
      title={isNew ? 'Add Scheduler Event' : `Edit ${initial!.eventId}`}
      panelClassName="max-w-xl"
    >
      <form
        onSubmit={(e) => { e.preventDefault(); if (canSave) mut.mutate(); }}
        className="space-y-4"
      >
        <div>
          <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Event ID</label>
          <input
            type="text"
            value={eventId}
            onChange={(e) => setEventId(e.target.value)}
            disabled={!isNew}
            required
            placeholder="e.g. devash.load_sheet.daily"
            className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-md px-3 py-2 font-mono disabled:opacity-60 disabled:cursor-not-allowed"
          />
          {!isNew && <div className="text-[10px] text-slate-600 mt-1">Event ID is immutable — delete + re-add to rename.</div>}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Send Type</label>
            <input
              type="text"
              list="scheduler-send-types"
              value={sendType}
              onChange={(e) => setSendType(e.target.value)}
              required
              className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-md px-3 py-2 font-mono"
            />
            <datalist id="scheduler-send-types">
              {SEND_TYPES.map(t => <option key={t} value={t} />)}
            </datalist>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Time of Day</label>
            <input
              type="time"
              value={timeHM}
              onChange={(e) => setTimeHM(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-md px-3 py-2 font-mono"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Days of Week</label>
          <div className="inline-flex gap-1">
            {DAY_LETTERS.map((letter, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setDays(prev => prev.map((d, j) => j === i ? !d : d))}
                className={`w-8 h-8 inline-flex items-center justify-center rounded-md font-mono text-xs transition-colors ${
                  days[i]
                    ? 'bg-violet-500/30 text-violet-100 border border-violet-500/40'
                    : 'bg-slate-800/60 text-slate-500 border border-slate-700/40 hover:text-slate-300'
                }`}
                title={DAY_NAMES[i]}
              >
                {letter}
              </button>
            ))}
          </div>
          <div className="text-[10px] text-slate-600 mt-1 font-mono">
            mask: {days.some(Boolean) ? serializeDaysMask(days) : '(none — fires daily)'}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Time Zone</label>
            <input
              type="text"
              value={timeZone}
              onChange={(e) => setTimeZone(e.target.value)}
              placeholder={defaultTimeZone ?? 'America/New_York'}
              className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-md px-3 py-2 font-mono"
            />
            <div className="text-[10px] text-slate-600 mt-1">
              IANA name. Tenant default: <span className="font-mono">{defaultTimeZone ?? '—'}</span>
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Active</label>
            <button
              type="button"
              onClick={() => setActive(a => !a)}
              className="flex items-center gap-2 px-3 py-2 text-sm rounded-md bg-slate-800 border border-slate-700 text-slate-200 w-full"
            >
              {active
                ? <ToggleRight size={20} className="text-emerald-400" />
                : <ToggleLeft size={20} className="text-slate-600" />}
              {active ? 'Active' : 'Paused'}
            </button>
          </div>
        </div>

        {mut.error && <ErrorBox err={mut.error} />}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-700/40">
          <button
            type="button"
            onClick={onClose}
            disabled={mut.isPending}
            className="px-4 py-2 text-sm text-slate-300 hover:text-white rounded-md hover:bg-slate-800/60 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSave}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-violet-500/20 border border-violet-500/40 text-violet-100 hover:bg-violet-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {mut.isPending ? <Loader2 size={14} className="animate-spin" /> : null}
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Audit tab (read-only) ────────────────────────────────────────

function AuditPanel({ slug }: { slug: string }) {
  const q = useQuery({
    queryKey: ['admin', 'audit', slug],
    queryFn: () => adminApi.getAudit({ limit: 50, slug }),
    refetchOnWindowFocus: false,
  });

  if (q.isLoading) return <PanelCard><div className="text-sm text-slate-500 flex items-center gap-2"><RefreshCw size={14} className="animate-spin" /> Loading...</div></PanelCard>;
  if (q.error)     return <PanelCard><ErrorBox err={q.error} /></PanelCard>;

  return (
    <PanelCard>
      <div className="flex items-center gap-2 mb-4">
        <Clock size={16} className="text-violet-300" />
        <h3 className="text-sm font-semibold text-white">Audit Log</h3>
        <span className="text-xs text-slate-500 ml-auto">last {q.data?.length ?? 0}</span>
      </div>
      {(!q.data || q.data.length === 0) && (
        <div className="text-sm text-slate-500 py-4">No audit entries yet.</div>
      )}
      {q.data && q.data.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 uppercase tracking-wider border-b border-slate-700/40">
            <tr>
              <th className="text-left py-2 font-medium">When</th>
              <th className="text-left py-2 font-medium">Action</th>
              <th className="text-left py-2 font-medium">Actor</th>
              <th className="text-left py-2 font-medium">Target</th>
            </tr>
          </thead>
          <tbody>
            {q.data.map((a) => (
              <tr key={a.id} className="border-b border-slate-700/20">
                <td className="py-2 text-xs text-slate-500 whitespace-nowrap">{fmtLocalTime(a.occurredAt)}</td>
                <td className="py-2">
                  <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-slate-800/60 text-slate-300 border border-slate-700/40">
                    {a.action}
                  </span>
                </td>
                <td className="py-2 text-xs text-slate-400">
                  {a.actorEmail || <span className="italic text-slate-600">unknown</span>}
                  {a.actorSub && <div className="text-[10px] text-slate-600 font-mono">{a.actorSub}</div>}
                </td>
                <td className="py-2 text-xs text-slate-300 font-mono break-all">{a.target ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </PanelCard>
  );
}

